import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../Modal';
import SearchableDropdown from '../SearchableDropdown';
import TaskAssigneePicker from '../TaskAssigneePicker';
import { SortableTh } from '../SortableTh';
import { useAuth } from '../../context/AuthContext';
import { useTableSortState } from '../../hooks/useTableSort';
import { sortRows } from '../../utils/tableSort';
import { makeAppointmentFilter } from '../../utils/appointmentSearch';
import { formatDateTimePT } from '../../utils/timezone';
import { dateTimeLocalToIso } from '../../utils/dateTimeInput';
import {
  createOnsiteServiceAppointment,
  fetchOnsiteServiceAppointments,
  type OnsiteServiceRow,
} from '../../api/onsiteServiceAppointments';
import { type ClientPickerRow } from '../../api/clients';
import { useClientPickerOptions } from '../../hooks/useClientPickerOptions';
import { fetchVerifiedUsers, type VerifiedUser } from '../../api/users';

/** "Aug 21, 2026, 9:00 AM", or a range when the visit spans more than one day. */
export function formatServiceWindow(startIso: string | null, endIso: string | null): string {
  const opts: Intl.DateTimeFormatOptions = { month: 'short', second: undefined };
  const start = formatDateTimePT(startIso, opts);
  if (!endIso) return start;
  return `${start} – ${formatDateTimePT(endIso, opts)}`;
}

function AppointmentsTable({
  title,
  sectionId,
  rows,
  emptyText,
  onRowClick,
  extraColumn,
}: {
  title: string;
  sectionId: string;
  rows: OnsiteServiceRow[];
  emptyText: string;
  onRowClick: (id: number) => void;
  extraColumn?: { label: string; key: string; render: (r: OnsiteServiceRow) => React.ReactNode };
}) {
  const headingId = `onsite-service-section-${sectionId}`;
  const { sortKey, sortDir, toggleSort } = useTableSortState();
  const sorted = useMemo(
    () =>
      sortRows(rows, sortKey, sortDir, (r, key) => {
        switch (key) {
          case 'job_code':
            return r.job_code ?? '';
          case 'business':
            return r.business_name ?? '';
          case 'reason':
            return r.reason ?? '';
          case 'appointment':
            return r.service_at ?? '';
          case 'tasks':
            return String(r.tasks.length);
          case extraColumn?.key:
            return String(extraColumn?.render(r) ?? '');
          default:
            return '';
        }
      }),
    [rows, sortKey, sortDir, extraColumn]
  );
  const colCount = 6 + (extraColumn ? 1 : 0);
  return (
    <section className="events-table-section" aria-labelledby={headingId}>
      <h2 className="events-section-title" id={headingId}>
        {title} <span className="events-section-count">({rows.length})</span>
      </h2>
      <div className="events-table-wrap">
        <table className="tasks-search-results-table">
          <thead>
            <tr>
              <SortableTh label="Job code" columnKey="job_code" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Business" columnKey="business" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Reason" columnKey="reason" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Visit" columnKey="appointment" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <th scope="col">Technicians</th>
              <SortableTh label="Tasks" columnKey="tasks" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              {extraColumn ? (
                <SortableTh
                  label={extraColumn.label}
                  columnKey={extraColumn.key}
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={toggleSort}
                />
              ) : null}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="events-empty-cell">
                  {emptyText}
                </td>
              </tr>
            ) : (
              sorted.map((r) => (
                <tr key={r.id} className="events-table-row-clickable" onClick={() => onRowClick(r.id)}>
                  <td data-label="Job code">
                    {r.job_code ? <span className="job-code">{r.job_code}</span> : '—'}
                  </td>
                  <td data-label="Business">{r.business_name}</td>
                  <td data-label="Reason">{r.reason || '—'}</td>
                  <td data-label="Visit">{formatServiceWindow(r.service_at, r.service_end_at)}</td>
                  <td data-label="Technicians">
                    {r.assignees.length ? r.assignees.map((a) => a.username).join(', ') : '—'}
                  </td>
                  <td data-label="Tasks">
                    {r.tasks.length === 0
                      ? '—'
                      : `${r.tasks.filter((t) => t.is_complete).length}/${r.tasks.length} done`}
                  </td>
                  {extraColumn ? <td data-label={extraColumn.label}>{extraColumn.render(r)}</td> : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

type CreateSource = 'manual' | 'client';

const OnsiteServiceTab: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [scheduled, setScheduled] = useState<OnsiteServiceRow[]>([]);
  const [completed, setCompleted] = useState<OnsiteServiceRow[]>([]);
  const [voided, setVoided] = useState<OnsiteServiceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [source, setSource] = useState<CreateSource>('client');
  const { clients, clientsLoading } = useClientPickerOptions();
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);

  const [businessName, setBusinessName] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [pointOfContact, setPointOfContact] = useState('');
  const [pocPhone, setPocPhone] = useState('');
  const [pocEmail, setPocEmail] = useState('');
  const [serviceAt, setServiceAt] = useState('');
  const [serviceEndAt, setServiceEndAt] = useState('');
  const [multiDay, setMultiDay] = useState(false);
  const [reason, setReason] = useState('');
  const [tasks, setTasks] = useState<string[]>(['']);
  const [assigneeIds, setAssigneeIds] = useState<number[]>([]);
  const [verifiedUsers, setVerifiedUsers] = useState<VerifiedUser[]>([]);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState('');

  const filterRows = useMemo(
    () =>
      makeAppointmentFilter<OnsiteServiceRow>(searchQuery, (r) => [
        r.job_code,
        r.business_name,
        r.business_address,
        r.point_of_contact,
        r.reason,
        r.created_by_username,
        ...r.assignees.map((a) => a.username),
        ...r.tasks.map((t) => t.description),
      ]),
    [searchQuery]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await fetchOnsiteServiceAppointments();
      setScheduled(data.scheduled);
      setCompleted(data.completed);
      setVoided(data.void);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load appointments.');
      setScheduled([]);
      setCompleted([]);
      setVoided([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    fetchVerifiedUsers()
      .then((list) => {
        if (!cancelled) setVerifiedUsers(list);
      })
      .catch(() => {
        if (!cancelled) setVerifiedUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const assigneePickerUsers = useMemo(() => {
    const hasRole = (u: VerifiedUser) => (u.roles ?? []).includes('installation');
    const ordered = [...verifiedUsers].sort((a, b) => {
      const ai = hasRole(a) ? 0 : 1;
      const bi = hasRole(b) ? 0 : 1;
      if (ai !== bi) return ai - bi;
      return a.username.localeCompare(b.username);
    });
    return ordered.map((u) => ({ id: u.id, username: u.username }));
  }, [verifiedUsers]);

  const resetForm = () => {
    setSource('client');
    setSelectedClientId(null);
    setBusinessName('');
    setBusinessAddress('');
    setPointOfContact('');
    setPocPhone('');
    setPocEmail('');
    setServiceAt('');
    setServiceEndAt('');
    setMultiDay(false);
    setReason('');
    setTasks(['']);
    setAssigneeIds([]);
    setAddError('');
  };

  const openAdd = () => {
    resetForm();
    setAddOpen(true);
  };

  const applyClient = (client: ClientPickerRow | null) => {
    if (!client) {
      setSelectedClientId(null);
      return;
    }
    setSelectedClientId(client.id);
    setBusinessName(client.company ?? '');
    setBusinessAddress(client.site_location || client.billing_address || '');
    setPointOfContact(client.point_of_contact ?? '');
    setPocPhone(client.contact_phone ?? '');
    setPocEmail(client.contact_email ?? '');
    setAddError('');
  };

  const submitAdd = async () => {
    if (!user?.id) return;
    const name = businessName.trim();
    if (!name) {
      setAddError('Business name is required.');
      return;
    }
    if (!reason.trim()) {
      setAddError('A reason for the visit is required.');
      return;
    }
    if (!serviceAt) {
      setAddError('Date and time of the visit is required.');
      return;
    }
    if (multiDay && !serviceEndAt) {
      setAddError('Enter when the visit ends, or switch back to a single day.');
      return;
    }
    if (assigneeIds.length === 0) {
      setAddError('Assign at least one technician.');
      return;
    }
    const taskList = tasks.map((t) => t.trim()).filter(Boolean);
    if (taskList.length === 0) {
      setAddError('List at least one task to accomplish during the visit.');
      return;
    }
    setAddSubmitting(true);
    setAddError('');
    try {
      const created = await createOnsiteServiceAppointment({
        user_id: user.id,
        client_id: source === 'client' ? selectedClientId : null,
        business_name: name,
        business_address: businessAddress.trim() || null,
        point_of_contact: pointOfContact.trim() || null,
        poc_phone: pocPhone.trim() || null,
        poc_email: pocEmail.trim() || null,
        service_at: dateTimeLocalToIso(serviceAt),
        service_end_at: multiDay ? dateTimeLocalToIso(serviceEndAt) : null,
        reason: reason.trim(),
        assignee_user_ids: assigneeIds,
        tasks: taskList,
      });
      setAddOpen(false);
      navigate(`/installation/onsite-service/${created.id}`);
    } catch (e) {
      setAddError(e instanceof Error ? e.message : 'Failed to create appointment.');
    } finally {
      setAddSubmitting(false);
    }
  };

  const clientOptions = useMemo(() => clients.map((c) => c.company), [clients]);
  const selectedClientDisplay = useMemo(
    () => clients.find((x) => x.id === selectedClientId)?.company ?? '',
    [clients, selectedClientId]
  );

  const openDetail = (id: number) => navigate(`/installation/onsite-service/${id}`);

  return (
    <>
      <div className="page-toolbar events-page-toolbar">
        <input
          type="text"
          className="page-toolbar-search"
          placeholder="Search by job code, business, reason…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="Search onsite service visits"
        />
        <button type="button" className="page-toolbar-btn page-toolbar-btn--centered" onClick={openAdd}>
          <span aria-hidden="true">+</span> Create appointment
        </button>
      </div>

      {loadError ? (
        <p className="create-order-error" role="alert">
          {loadError}
        </p>
      ) : null}

      {loading ? (
        <p className="page-subtitle">Loading appointments…</p>
      ) : (
        <>
          <AppointmentsTable
            title="Scheduled Visits"
            sectionId="scheduled"
            rows={filterRows(scheduled)}
            emptyText="No scheduled onsite service visits."
            onRowClick={openDetail}
          />
          <AppointmentsTable
            title="Completed Visits"
            sectionId="completed"
            rows={filterRows(completed)}
            emptyText="No completed visits."
            onRowClick={openDetail}
            extraColumn={{
              label: 'Completed',
              key: 'completed',
              render: (r) => formatDateTimePT(r.completed_at, { month: 'short', second: undefined }),
            }}
          />
          <AppointmentsTable
            title="Voided Visits"
            sectionId="void"
            rows={filterRows(voided)}
            emptyText="No voided visits."
            onRowClick={openDetail}
            extraColumn={{ label: 'Void reason', key: 'void_reason', render: (r) => r.void_reason || '—' }}
          />
        </>
      )}

      <Modal isOpen={addOpen} onClose={() => setAddOpen(false)} title="Create onsite service appointment" wide>
        <div className="modal-body">
          <div className="form-group">
            <span className="form-label">Source</span>
            <div className="elevators-source-row">
              <label className="elevators-radio-label">
                <input
                  type="radio"
                  name="onsite-source"
                  checked={source === 'client'}
                  onChange={() => setSource('client')}
                  disabled={addSubmitting}
                />
                Select a client from the database
              </label>
              <label className="elevators-radio-label">
                <input
                  type="radio"
                  name="onsite-source"
                  checked={source === 'manual'}
                  onChange={() => {
                    setSource('manual');
                    setSelectedClientId(null);
                  }}
                  disabled={addSubmitting}
                />
                Enter business information
              </label>
            </div>
          </div>

          {source === 'client' ? (
            <div className="form-group">
              <span className="form-label">Client</span>
              <SearchableDropdown
                options={clientOptions}
                value={selectedClientDisplay}
                onChange={(display) => applyClient(clients.find((x) => x.company === display) ?? null)}
                placeholder={clientsLoading ? 'Loading clients…' : 'Type to search clients…'}
                disabled={addSubmitting || clientsLoading}
                noResultsMessage="No matching client"
              />
            </div>
          ) : null}

          <div className="form-group">
            <label htmlFor="onsite-business-name" className="form-label">
              Business Name
            </label>
            <input
              id="onsite-business-name"
              type="text"
              className="form-input"
              value={businessName}
              onChange={(e) => {
                setBusinessName(e.target.value);
                setAddError('');
              }}
              placeholder="e.g. Grand Plaza Hotel"
              disabled={addSubmitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="onsite-business-address" className="form-label">
              Business Address
            </label>
            <input
              id="onsite-business-address"
              type="text"
              className="form-input"
              value={businessAddress}
              onChange={(e) => setBusinessAddress(e.target.value)}
              placeholder="Street, City, State, ZIP"
              disabled={addSubmitting}
            />
          </div>
          <div className="elevators-form-row">
            <div className="form-group">
              <label htmlFor="onsite-poc" className="form-label">
                Point of Contact
              </label>
              <input
                id="onsite-poc"
                type="text"
                className="form-input"
                value={pointOfContact}
                onChange={(e) => setPointOfContact(e.target.value)}
                disabled={addSubmitting}
              />
            </div>
            <div className="form-group">
              <label htmlFor="onsite-poc-phone" className="form-label">
                Point of Contact's Number
              </label>
              <input
                id="onsite-poc-phone"
                type="tel"
                className="form-input"
                value={pocPhone}
                onChange={(e) => setPocPhone(e.target.value)}
                disabled={addSubmitting}
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="onsite-poc-email" className="form-label">
              Point of Contact's Email Address
            </label>
            <input
              id="onsite-poc-email"
              type="email"
              className="form-input"
              value={pocEmail}
              onChange={(e) => setPocEmail(e.target.value)}
              disabled={addSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="onsite-reason" className="form-label">
              Reason for the visit
            </label>
            <textarea
              id="onsite-reason"
              className="form-textarea"
              rows={2}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setAddError('');
              }}
              placeholder="e.g. Robot repeatedly losing its network drop"
              disabled={addSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="onsite-at" className="form-label">
              {multiDay ? 'Visit starts' : 'Date & Time of Visit'}
            </label>
            <input
              id="onsite-at"
              type="datetime-local"
              className="form-input"
              value={serviceAt}
              onChange={(e) => {
                setServiceAt(e.target.value);
                setAddError('');
              }}
              disabled={addSubmitting}
            />
          </div>
          <div className="form-group">
            <label className="elevators-radio-label">
              <input
                type="checkbox"
                checked={multiDay}
                onChange={(e) => {
                  setMultiDay(e.target.checked);
                  if (!e.target.checked) setServiceEndAt('');
                  setAddError('');
                }}
                disabled={addSubmitting}
              />
              This visit spans a date range
            </label>
          </div>
          {multiDay ? (
            <div className="form-group">
              <label htmlFor="onsite-end-at" className="form-label">
                Visit ends
              </label>
              <input
                id="onsite-end-at"
                type="datetime-local"
                className="form-input"
                value={serviceEndAt}
                onChange={(e) => {
                  setServiceEndAt(e.target.value);
                  setAddError('');
                }}
                disabled={addSubmitting}
              />
            </div>
          ) : null}

          <div className="form-group">
            <span className="form-label">Tasks to accomplish during the visit</span>
            <div className="onsite-task-inputs">
              {tasks.map((task, index) => (
                <div className="onsite-task-input-row" key={index}>
                  <input
                    type="text"
                    className="form-input"
                    value={task}
                    onChange={(e) => {
                      const next = [...tasks];
                      next[index] = e.target.value;
                      setTasks(next);
                      setAddError('');
                    }}
                    placeholder={`Task ${index + 1}`}
                    disabled={addSubmitting}
                    aria-label={`Task ${index + 1}`}
                  />
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => setTasks(tasks.length === 1 ? [''] : tasks.filter((_, i) => i !== index))}
                    disabled={addSubmitting}
                    aria-label={`Remove task ${index + 1}`}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="elevator-btn"
              onClick={() => setTasks([...tasks, ''])}
              disabled={addSubmitting}
            >
              + Add task
            </button>
          </div>

          <div className="form-group">
            <span className="form-label">Technician(s)</span>
            <p className="elevators-confirm-hint" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
              Employees with the Installation role are listed first.
            </p>
            <TaskAssigneePicker
              users={assigneePickerUsers}
              value={assigneeIds}
              onChange={setAssigneeIds}
              addPlaceholder="Search employee to assign…"
            />
          </div>

          {addError ? (
            <p className="create-order-error" role="alert">
              {addError}
            </p>
          ) : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={addSubmitting} onClick={() => setAddOpen(false)}>
            Cancel
          </button>
          <button type="button" className="update-button" disabled={addSubmitting} onClick={() => void submitAdd()}>
            {addSubmitting ? 'Creating…' : 'Confirm & create'}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default OnsiteServiceTab;
